import type { FunctionComponent, PropsWithChildren } from 'react';
import {
  Pagination,
  type StringIndexedObject,
} from '@kanda-libs/ks-design-library';
import type { Column } from 'react-table';
import HeaderGroup from './components/HeaderGroup';
import Pages from './components/Pages';
import useTableProps from './useTableProps';

export interface TableProps<T extends StringIndexedObject<unknown>> {
  columns: Readonly<Column<T>[]>;
  data: T[];
  filter?: string;
  pageSize?: number;
  compact?: boolean;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  query?: string;
}

export type TableFunctionComponent<
  T extends StringIndexedObject<unknown> = any,
> = FunctionComponent<TableProps<T>>;

function Table<T extends StringIndexedObject<unknown>>({
  compact = false,
  isLoading = false,
  onRowClick,
  ...props
}: PropsWithChildren<TableProps<T>>) {
  const {
    page,
    prepareRow,
    columns,
    getTableProps,
    headerGroups,
    paginationProps,
  } = useTableProps(props);

  return (
    <>
      <div {...getTableProps()} className="w-full">
        <div>
          {headerGroups.map((headerGroup) => (
            <HeaderGroup
              headerGroup={headerGroup}
              columns={columns}
              isLoading={isLoading}
            />
          ))}
        </div>
        <Pages
          page={page}
          prepareRow={prepareRow}
          isLoading={isLoading}
          compact={compact}
          onRowClick={onRowClick}
        />
      </div>
      {paginationProps.pageCount > 1 && (
        <div className="px-4 py-4">
          <Pagination {...paginationProps} />
        </div>
      )}
    </>
  );
}

export default Table;
